import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Reservation } from '../../../models/sala.model';
import { formatDayLabel, formatTimeRange, paymentLabel, peopleLabel, reservationStatusLabel, reservationTone, resolveDashboardImage, timeUntil } from './dashboard-view.util';

/**
 * Tarjeta de la próxima reserva relevante, con la foto del espacio y los datos
 * reales de la reserva. Si no hay ninguna, muestra un estado vacío con salida
 * al calendario.
 */
@Component({
  selector: 'app-dashboard-next-reservation',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="next" aria-labelledby="next-title">
      <header>
        <h3 id="next-title">Próxima reserva</h3>
        <button type="button" class="link" (click)="calendar.emit()">Ver todas <i class="bi bi-arrow-right" aria-hidden="true"></i></button>
      </header>

      <ng-container *ngIf="reservation; else empty">
        <div class="photo">
          <img *ngIf="resolvedImage as imageUrl" [src]="imageUrl" [alt]="'Espacio ' + reservation.space_title" loading="lazy" (error)="onImageError()" />
          <div class="photo-empty" *ngIf="!resolvedImage" role="img" [attr.aria-label]="reservation.space_title + ' no tiene fotografia'">
            <span aria-hidden="true"><i class="bi bi-building"></i></span>
            <small>Espacio sin fotografía</small>
          </div>
          <span class="badge" *ngIf="when">
            <i class="bi bi-clock-history" aria-hidden="true"></i> {{ when }}
          </span>
        </div>
        <strong class="title">{{ reservation.customer_name || 'Reserva' }}</strong>
        <ul class="meta">
          <li><i class="bi bi-building" aria-hidden="true"></i>{{ reservation.space_title }}</li>
          <li><i class="bi bi-calendar-event" aria-hidden="true"></i>{{ day }}</li>
          <li><i class="bi bi-clock" aria-hidden="true"></i>{{ range }}</li>
          <li><i class="bi bi-people" aria-hidden="true"></i>{{ people }}</li>
          <li><i class="bi bi-info-circle" aria-hidden="true"></i>{{ status }} · {{ pay }}</li>
          <li *ngIf="customer"><i class="bi bi-person" aria-hidden="true"></i>{{ customer }}</li>
        </ul>
        <div class="actions">
          <button type="button" class="primary" (click)="details.emit(reservation)">Ver detalles</button>
          <button type="button" class="ghost" *ngIf="canContact" (click)="contact.emit(reservation)">
            <i class="bi bi-chat-dots" aria-hidden="true"></i> Contactar cliente
          </button>
        </div>
      </ng-container>

      <ng-template #empty>
        <div class="empty">
          <span class="empty-icon" aria-hidden="true"><i class="bi bi-calendar2-week"></i></span>
          <strong>No tienes reservas próximas.</strong>
          <p>Cuando entre una reserva, aparecerá aquí con todos sus datos.</p>
          <button type="button" class="primary" (click)="calendar.emit()">Ver calendario</button>
        </div>
      </ng-template>
    </section>
  `,
  styles: [`
    :host { display: block; height: 100%; }
    .next { box-sizing: border-box; height: 100%; padding: 1rem 1.05rem 1.1rem; border: 1px solid var(--ts-card-border, var(--ts-border)); border-radius: var(--ts-radius-lg); background: var(--ts-surface); box-shadow: var(--ts-card-shadow, var(--ts-shadow-sm)); }
    header { display: flex; align-items: center; justify-content: space-between; margin-bottom: .7rem; }
    h3 { margin: 0; color: var(--ts-text); font-family: "Plus Jakarta Sans", Inter, sans-serif; font-size: .92rem; letter-spacing: -.02em; }
    .link { display: inline-flex; align-items: center; gap: .3rem; color: var(--ts-text-blue-300-4); border: 0; background: transparent; font: inherit; font-size: .68rem; font-weight: 750; cursor: pointer; }
    .link:hover { text-decoration: underline; }
    .photo { position: relative; height: 132px; margin-bottom: .7rem; overflow: hidden; border-radius: var(--ts-radius-md); background: var(--ts-surface-2); }
    .photo img { width: 100%; height: 100%; object-fit: cover; }
    .photo-empty { display: grid; place-content: center; justify-items: center; gap: .45rem; width: 100%; height: 100%; color: var(--ts-text-muted); background: linear-gradient(145deg, color-mix(in srgb, var(--ts-primary) 8%, var(--ts-surface)), var(--ts-surface-2)); }
    .photo-empty > span { position: relative; display: grid; place-items: center; width: 54px; height: 54px; color: var(--ts-text-blue-300-4); border: 1px solid color-mix(in srgb, var(--ts-primary) 22%, var(--ts-border)); border-radius: 17px; background: color-mix(in srgb, var(--ts-primary) 10%, var(--ts-surface)); font-size: 1.25rem; }
    .photo-empty small { font-size: .62rem; font-weight: 700; }
    .badge { position: absolute; left: .55rem; bottom: .55rem; display: inline-flex; align-items: center; gap: .3rem; padding: .28rem .55rem; color: #fff; border-radius: 999px; background: linear-gradient(135deg, var(--ts-primary), var(--ts-indigo)); font-size: .63rem; font-weight: 750; box-shadow: 0 4px 12px var(--ts-shadow-gray-1000-a28); }
    .title { display: block; margin-bottom: .45rem; color: var(--ts-text); font-size: .88rem; }
    .meta { display: grid; gap: .3rem; margin: 0 0 .85rem; padding: 0; list-style: none; }
    .meta li { display: flex; align-items: center; gap: .45rem; color: var(--ts-text-muted); font-size: .69rem; }
    .meta i { color: var(--ts-text-blue-300-4); font-size: .78rem; }
    .actions { display: flex; gap: .5rem; }
    .actions button, .empty button { min-height: 36px; border-radius: 9px; font: inherit; font-size: .72rem; font-weight: 750; cursor: pointer; }
    .actions .primary, .empty .primary { flex: 1 1 0; color: var(--ts-on-primary); border: 0; background: linear-gradient(135deg, var(--ts-primary), var(--ts-indigo)); }
    .actions .ghost { flex: 1 1 0; display: inline-flex; align-items: center; justify-content: center; gap: .35rem; color: var(--ts-text); border: 1px solid var(--ts-border); background: transparent; }
    .actions .ghost:hover { border-color: var(--ts-border-blue-400-a40); background: var(--ts-surface-hover); }
    .empty { display: grid; justify-items: center; gap: .4rem; padding: 1.1rem .5rem; text-align: center; }
    .empty-icon { display: grid; place-items: center; width: 48px; height: 48px; color: var(--ts-text-blue-300-4); border-radius: 50%; background: var(--ts-bg-blue-400-a19); font-size: 1.2rem; }
    .empty strong { color: var(--ts-text); font-size: .82rem; }
    .empty p { margin: 0 0 .3rem; color: var(--ts-text-muted); font-size: .68rem; line-height: 1.45; }
    .empty .primary { padding: 0 1rem; }
  `],
})
export class DashboardNextReservationComponent implements OnChanges {
  @Input() reservation: Reservation | null = null;
  @Input() imageUrl: string | null = null;
  @Output() details = new EventEmitter<Reservation>();
  @Output() contact = new EventEmitter<Reservation>();
  @Output() calendar = new EventEmitter<void>();

  get day(): string { return formatDayLabel(this.dayIso); }
  imageFailed = false;
  ngOnChanges(): void { this.imageFailed = false; }
  get resolvedImage(): string | null {
    return !this.imageFailed && this.imageUrl ? resolveDashboardImage(this.imageUrl, '') : null;
  }
  get range(): string { return formatTimeRange(this.reservation?.start_time ?? null, this.reservation?.end_time ?? null); }
  get people(): string { return peopleLabel(this.reservation?.number_of_people ?? 0); }
  get status(): string { return this.reservation ? reservationStatusLabel(this.reservation) : ''; }
  get pay(): string { return this.reservation ? paymentLabel(this.reservation) : ''; }
  get tone() { return this.reservation ? reservationTone(this.reservation) : 'blue'; }
  get when(): string { return this.reservation ? timeUntil(this.dayIso, this.reservation.start_time) : ''; }
  get customer(): string {
    const r = this.reservation;
    if (!r) return '';
    return r.customer_name || r.guest_name || '';
  }
  get canContact(): boolean {
    const r = this.reservation;
    if (!r) return false;
    return !!(r.customer?.email || r.customer?.phone_number || r.guest_email || r.guest_phone);
  }

  private get dayIso(): string {
    const r = this.reservation;
    return (r?.reservation_date || r?.date || '').slice(0, 10);
  }

  onImageError(): void { this.imageFailed = true; }
}
